import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, ArrowRight } from "lucide-react";

function todayPlus(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

/**
 * A refined floating booking card that sits directly beneath the hero video.
 * Collects the three most important pieces of intent (arrival, departure, guests)
 * and hands off to the full booking page for room selection and deposit.
 */
export default function HomeBookingCard() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        check_in: todayPlus(14),
        check_out: todayPlus(19),
        guests: 2,
    });
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = (e) => {
        e.preventDefault();
        const params = new URLSearchParams({
            check_in: form.check_in,
            check_out: form.check_out,
            guests: String(form.guests),
        });
        navigate(`/booking?${params.toString()}`);
    };

    return (
        <section data-testid="home-booking-card" className="relative z-30 max-w-6xl mx-auto px-6 -mt-16 md:-mt-20">
            <form
                onSubmit={submit}
                className="bg-ivory rounded-2xl shadow-2xl shadow-navy/25 border border-navy/5 overflow-hidden"
            >
                <div className="grid grid-cols-1 md:grid-cols-4 md:divide-x md:divide-navy/10">
                    <label className="px-6 py-6 md:py-7 flex flex-col text-navy">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/50 flex items-center gap-1.5 mb-1.5">
                            <CalendarDays size={12} /> Arrival
                        </span>
                        <input
                            data-testid="home-checkin-input"
                            type="date"
                            value={form.check_in}
                            onChange={update("check_in")}
                            className="bg-transparent outline-none text-base font-medium"
                        />
                    </label>
                    <label className="px-6 py-6 md:py-7 flex flex-col text-navy border-t md:border-t-0 border-navy/10">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/50 flex items-center gap-1.5 mb-1.5">
                            <CalendarDays size={12} /> Departure
                        </span>
                        <input
                            data-testid="home-checkout-input"
                            type="date"
                            value={form.check_out}
                            onChange={update("check_out")}
                            className="bg-transparent outline-none text-base font-medium"
                        />
                    </label>
                    <label className="px-6 py-6 md:py-7 flex flex-col text-navy border-t md:border-t-0 border-navy/10">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/50 flex items-center gap-1.5 mb-1.5">
                            <Users size={12} /> Guests
                        </span>
                        <select
                            data-testid="home-guests-input"
                            value={form.guests}
                            onChange={update("guests")}
                            className="bg-transparent outline-none text-base font-medium"
                        >
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <option key={n} value={n}>{`${n} guest${n > 1 ? "s" : ""}`}</option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="submit"
                        data-testid="home-book-cta"
                        className="btn-gold flex items-center justify-center gap-2 px-8 py-6 md:py-0 w-full"
                    >
                        Book Your Stay <ArrowRight size={16} />
                    </button>
                </div>
            </form>
        </section>
    );
}
